import * as Yup from "yup";
import { SelectedItem } from "../../../../../utils/utils";
import {
  Address,
  ClaimPlaceOfServiceEnum,
  PatientProcedureCode,
  SuperBillPlaceOfServiceEnum,
} from "../../../../../services";
import { isTelehealthPlaceOfService } from "../use-service-location-billing";
import { getSelectedModifiers, hasAnyDiagnosisPointer } from "./claims-utils";
import { Option } from "../../../../../constants/common";
import { AsyncSelectValue } from "../../../../../components/AsyncSelect";
type PatientListOption = {
  label: string;
  value: boolean;
  birthDate: string;
};

export type CLAIM_FORM_TYPE = {
  uuid?: string;
  patient?: PatientListOption;
  encounter?: {
    value: string;
    label: string;
  };
  serviceLocation?: SelectedItem<string>;
  placeOfService?: SuperBillPlaceOfServiceEnum | "";
  dateOfService?: string;
  provider?: AsyncSelectValue;
  referringProvider?: AsyncSelectValue;
  /** Optional; auto-filled from the referring provider profile and editable. */
  referringProviderNPINumber?: string;
  billingProvider?: AsyncSelectValue;
  encounterType?: string;
  payerInsuance?: any;
  insuranceId?: string;
  reasonForVisit?: string;
};

export const INITIALVALUES: CLAIM_FORM_TYPE = {
  uuid: undefined,
  patient: undefined,
  encounter: undefined,
  serviceLocation: undefined,
  placeOfService: undefined,
  dateOfService: undefined,
  provider: undefined,
  referringProvider: undefined,
  referringProviderNPINumber: "",
  billingProvider: undefined,
  encounterType: undefined,
  payerInsuance: undefined,
  insuranceId: "",
  reasonForVisit: "",
};

/**
 * Service Location and Rendering Provider are held as `{ uuid: name }` maps,
 * and an unset one reaches the form as an empty object — which satisfies Yup's
 * `required`. The mandatory check has to reject `{}` explicitly, or it never
 * fires.
 */
export const isSelected = (value?: object) =>
  Object.keys(value ?? {}).length > 0;

const SERVICE_LOCATION_REQUIRED_MESSAGE =
  "Service Location is a required field";

export const RENDERING_PROVIDER_REQUIRED_MESSAGE =
  "Rendering Provider is required.";

export const ADD_NEW_CLAIM_VALIDATION_SCHEMA = Yup.object({
  patient: Yup.object().required().label("Patient"),
  // encounter: Yup.object().required().label("Encounter"),
  // ECRH-5370: Service Location is optional for Telehealth encounters.
  serviceLocation: Yup.object()
    .label("Service Location")
    .when("placeOfService", {
      is: (pos: string) => !isTelehealthPlaceOfService(pos),
      then: (schema) =>
        schema
          .required()
          .test(
            "service-location-selected",
            SERVICE_LOCATION_REQUIRED_MESSAGE,
            isSelected
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  placeOfService: Yup.string().required().label("Place of Service"),
  dateOfService: Yup.string().required().label("Date of Service"),
  provider: Yup.object().required().label("Rendering Provider"),
  // Billing Provider = Practice Group (Billing Entity), auto-populated via
  // practiceName; no user input, so no required validation.
  // encounterType: Yup.object().required().label(""),
  payerInsuance: Yup.object().required().label("Insurance"),
  reasonForVisit: Yup.string().required().label("Reason for Visit"),
  // Referring Provider NPI stays optional, but a value that is entered must be
  // a valid 10-digit number, the same rule the Update Claim form applies to its
  // optional NPIs.
  referringProviderNPINumber: Yup.string().matches(/^\d{10}$/, {
    message: "NPI must be a valid 10-digit number.",
    excludeEmptyString: true,
  }),
});

export type UPDATE_CLAIM_FORM_TYPE = {
  uuid?: string;
  claimStatus?: string;
  patient?: SelectedItem<string>;
  provider?: SelectedItem<string>; // we need to remove this filed and use referring provider instead in service details section.
  referringProvider?: SelectedItem<string>;
  serviceLocation?: SelectedItem<string>;
  encounterType?: string;
  patientInsurance?: {
    uuid?: string;
    planName?: string;
    groupId?: string;
    groupName?: string;
    memberId?: string;
    faxNumber?: string;
    phoneNumber?: string;
    insurancePayer?: {
      payerId?: string;
      payerName?: string;
    };
    insuredFirstName?: string;
    insuredLastName?: string;
    planId?: string;
    insuredGender?: string;
    coPay?: string;
    insuranceId?: string;
    claimNumber?: string;
    planType?: string;
  };
  reasonForVisit?: string;
  active?: boolean;
  archive?: boolean;
  providerName?: string;
  referringProviderName?: string;
  insuranceId?: string;
  conditionRelatedToEmployment?: boolean;
  conditionRelatedToAutoAccident?: boolean;
  conditionRelatedToOtherAccident?: boolean;
  dateOfCurrentIllness?: string;
  otherDate?: string;
  unableToWorkFrom?: string;
  unableToWorkTo?: string;
  hospitalizationFrom?: string;
  hospitalizationTo?: string;
  state?: string;
  claimCodes?: string;
  acceptAssignment?: string;
  otherClaimId?: string;
  resubmissionCode?: string;
  originalReferenceNumber?: string;
  patientProcedureCodes?: any;
  diagnosisCodes?: SelectedItem<string>;
  encounter?: SelectedItem<string>;

  // service details section
  providerId?: { [key: string]: string };
  locationId?: { [key: string]: string };
  placeOfService?: ClaimPlaceOfServiceEnum | "";
  dateOfService: string | null;
  serviceState: "";
  // ECRH-5370: Billing Address resolved from the selected Service Location, or
  // from the Provider Group Profile when no location is selected.
  serviceLocationAddress?: Address | null;
  renderingproviderId?: { [key: string]: string };
  renderingProviderNPINumber: string;
  priorAuthorization: string;
  diagnisisCodeData: any;
  // Provider Details
  billingProviderId?: { [key: string]: string };
  billingProvideNPINumber: string;
  // Billing Provider = Practice Group (Billing Entity), resolved onto the claim.
  practiceName?: string;
  practiceNpi?: string;
  referringProviderId?: { [key: string]: string };
  referringProviderNPINumber: string;
  orderingProviderId?: { [key: string]: string };
  orderingProviderNPINumber: string;
};

export const UPDATE_FORM_INITIAL_VALUES: UPDATE_CLAIM_FORM_TYPE = {
  uuid: undefined,
  patient: undefined,
  provider: undefined, // we need to remove this filed and use referring provider instead in service details section.
  referringProvider: undefined,
  serviceLocation: undefined,
  encounterType: undefined,
  patientInsurance: undefined,

  diagnisisCodeData: undefined,

  conditionRelatedToEmployment: false,
  conditionRelatedToAutoAccident: false,
  conditionRelatedToOtherAccident: false,
  dateOfCurrentIllness: undefined,
  otherDate: undefined,
  claimCodes: undefined,
  state: "",

  unableToWorkFrom: undefined,
  unableToWorkTo: undefined,
  hospitalizationFrom: undefined,
  hospitalizationTo: undefined,

  resubmissionCode: undefined,
  originalReferenceNumber: undefined,

  acceptAssignment: undefined,
  otherClaimId: undefined,

  // Service Details
  providerId: undefined,
  locationId: undefined,
  placeOfService: "",
  dateOfService: "",
  serviceState: "",
  serviceLocationAddress: undefined,
  renderingproviderId: undefined,
  renderingProviderNPINumber: "",
  priorAuthorization: "",

  // Provider Details
  billingProviderId: undefined,
  billingProvideNPINumber: "",
  practiceName: "",
  practiceNpi: "",
  referringProviderId: undefined,
  referringProviderNPINumber: "",
  orderingProviderId: undefined,
  orderingProviderNPINumber: "",
};

export const UPDATE_FORM_VALIDATION = Yup.object({
  state: Yup.string()
    .label("State")
    .when("conditionRelatedToAutoAccident", {
      is: true,
      then: (schema) =>
        schema.required("Select state where auto accident occurred."),
      otherwise: (schema) => schema.notRequired(),
    }),
  // ECRH-5370: Service Location is optional for Telehealth encounters.
  locationId: Yup.object()
    .label("Service Location")
    .when("placeOfService", {
      is: (pos: string) => !isTelehealthPlaceOfService(pos),
      then: (schema) =>
        schema
          .required()
          .test(
            "service-location-selected",
            SERVICE_LOCATION_REQUIRED_MESSAGE,
            isSelected
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  placeOfService: Yup.string().required().label("Place of Service"),
  dateOfService: Yup.string().required().label("Date of Service"),
  serviceState: Yup.string().required().label("Service State"),
  renderingproviderId: Yup.object().test(
    "rendering-provider-selected",
    RENDERING_PROVIDER_REQUIRED_MESSAGE,
    isSelected
  ),
  // NPI numbers must be a valid 10-digit numeric value (ECRH-5217).
  renderingProviderNPINumber: Yup.string()
    .required("Rendering Provider NPI is required.")
    .matches(/^\d{10}$/, "NPI must be a valid 10-digit number."),
  billingProvideNPINumber: Yup.string().matches(/^\d{10}$/, {
    message: "NPI must be a valid 10-digit number.",
    excludeEmptyString: true,
  }),
  orderingProviderNPINumber: Yup.string().matches(/^\d{10}$/, {
    message: "NPI must be a valid 10-digit number.",
    excludeEmptyString: true,
  }),
  referringProviderNPINumber: Yup.string().matches(/^\d{10}$/, {
    message: "NPI must be a valid 10-digit number.",
    excludeEmptyString: true,
  }),
});

export const UPDATE_CLAIM_PROCEDURE_INITIAL_STATE: PatientProcedureCode[] = [
  {
    uuid: undefined,
    procedureCode: undefined,
    diagnosisFirstPointers: "",
    diagnosisSecondPointers: "",
    diagnosisThirdPointers: "",
    diagnosisFourthPointers: "",
    medicalCodeModifiers: "",
    modifiers: undefined,
    quantity: 1,
    total: 0,
    charges: 0,
    discount: 0,
    discountType: undefined,
  },
];

/**
 * Generic banner shown when the form is saved with mandatory fields missing.
 * The fields themselves carry the detail (asterisk + red border + field-level
 * message), so the snackbar must never concatenate the individual failures.
 */
export const CLAIM_REQUIRED_FIELDS_MESSAGE =
  "Please fill mandatory/required fields";

export const CLAIM_PROCEDURE_MESSAGES = {
  CPT_CODE_REQUIRED: "At least one CPT/HCPCS procedure code is required.",
  DIAGNOSIS_POINTER_REQUIRED: "At least one diagnosis pointer is required.",
  DUPLICATE_MODIFIER: "Duplicate modifiers are not allowed.",
  QUANTITY_MIN: "Quantity must be 1 or greater.",
  CHARGES_REQUIRED: "Charge amount is required.",
} as const;

/**
 * Blank label rendered above the Quantity / Charges / Total group of a CPT
 * line. Modifiers and Diagnosis Pointer both open with a label, so the spacer
 * keeps all three columns' inputs on one line once error messages appear.
 */
export const CPT_FIELD_GROUP_SPACER_LABEL = "\u00A0";

/**
 * Charges field of a CPT line. The $ adornment and the clear icon both eat into
 * the field, which clipped the cents off a formatted amount (125.00) — so the
 * padding around the value is trimmed instead of the column being widened.
 */
export const CHARGES_FIELD_SX = {
  width: "120px",
  padding: "0px",
  fontSize: "0.875rem",
  // 2px on the right keeps the clear icon inside the field border.
  "& .MuiInputBase-root": { paddingLeft: "8px", paddingRight: "2px" },
  "& .MuiInputAdornment-positionStart": { marginRight: "4px" },
  "& .MuiInputBase-input": { paddingLeft: "0px", paddingRight: "0px" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
};

/** Anchor used to scroll the CPT section into view when a line is invalid. */
export const PROCEDURE_CODE_SECTION_ID = "Procedure Code";

/**
 * Row fields the CPT grid reads validation state off, and the keys the schema
 * below writes its errors to. The Diagnosis Pointer group carries its error on
 * the first pointer.
 */
export const PROCEDURE_ROW_ERROR_KEYS = {
  DIAGNOSIS_POINTER: "diagnosisFirstPointers",
  MODIFIERS: "modifiers",
  QUANTITY: "quantity",
  CHARGES: "charges",
} as const;

export type ProcedureRowErrorKey =
  (typeof PROCEDURE_ROW_ERROR_KEYS)[keyof typeof PROCEDURE_ROW_ERROR_KEYS];

/**
 * Before a claim is moved to Ready to Submit or submitted to the clearinghouse,
 * every CPT/HCPCS line must carry a diagnosis pointer (ICD), a quantity of at
 * least 1 and a charge amount. Modifiers are optional — a line may be saved and
 * submitted without one — but when supplied they must not repeat on the same
 * line.
 *
 * Driven by Yup on the CPT-row Formik so every failure lands on the field that
 * owns it (`errors[rowIndex].<field>`) and the grid can render it inline,
 * instead of being flattened into one banner message.
 */
export const UPDATE_CLAIM_PROCEDURE_VALIDATION = Yup.array().of(
  Yup.object({
    // "at least one of the four pointers" is a row-level rule; it is hung on the
    // first pointer so the Diagnosis Pointer group has a single error to show.
    [PROCEDURE_ROW_ERROR_KEYS.DIAGNOSIS_POINTER]: Yup.string().test(
      "diagnosis-pointer-required",
      CLAIM_PROCEDURE_MESSAGES.DIAGNOSIS_POINTER_REQUIRED,
      (_value, context) => hasAnyDiagnosisPointer(context.parent)
    ),
    [PROCEDURE_ROW_ERROR_KEYS.MODIFIERS]: Yup.mixed().test(
      "no-duplicate-modifiers",
      CLAIM_PROCEDURE_MESSAGES.DUPLICATE_MODIFIER,
      (value) => {
        const selected = getSelectedModifiers(value);
        return new Set(selected).size === selected.length;
      }
    ),
    // Quantity 0 (or blank) zeroes the line total, which let a zero-charge claim
    // through Save / Save & Submit. A billable line must carry at least 1 unit.
    [PROCEDURE_ROW_ERROR_KEYS.QUANTITY]: Yup.number()
      .typeError(CLAIM_PROCEDURE_MESSAGES.QUANTITY_MIN)
      .min(1, CLAIM_PROCEDURE_MESSAGES.QUANTITY_MIN)
      .required(CLAIM_PROCEDURE_MESSAGES.QUANTITY_MIN),
    [PROCEDURE_ROW_ERROR_KEYS.CHARGES]: Yup.number()
      .typeError(CLAIM_PROCEDURE_MESSAGES.CHARGES_REQUIRED)
      .moreThan(0, CLAIM_PROCEDURE_MESSAGES.CHARGES_REQUIRED)
      .required(CLAIM_PROCEDURE_MESSAGES.CHARGES_REQUIRED),
  })
);

export const NEW_DATE_RANGE_FOR_CLAIMS_TYPE: Option[] = [
  {
    value: "TODAY",
    label: "Today",
  },
  {
    value: "YESTERDAY",
    label: "Yesterday",
  },
  {
    value: "LAST_7_DAYS",
    label: "Last 7 Days",
  },
  {
    value: "LAST_30_DAYS",
    label: "Last 30 Days",
  },
  {
    value: "LAST_90_DAYS",
    label: "Last 90 Days",
  },
  {
    value: "THIS_MONTH",
    label: "This Month",
  },
  {
    value: "LAST_MONTH",
    label: "Last Month",
  },
  {
    value: "CUSTOM_RANGE",
    label: "Custom Range",
  },
];

export const CLAIMS_STATUS_LIST: Option[] = [
  {
    value: "DRAFT",
    label: "Draft Claims",
  },
  {
    value: "READY_FOR_SUBMIT",
    label: "Ready for Submit",
  },
  {
    value: "SUBMITTED",
    label: "Submitted",
  },
  {
    value: "ACCEPTED",
    label: "Accepted",
  },
  {
    value: "PAID",
    label: "Paid",
  },
  {
    value: "DENIED",
    label: "Denied",
  },

  {
    value: "REJECTED",
    label: "Rejected",
  },
];

// Electronic / Paper
export const CLAIM_TYPE_LIST: Option[] = [
  {
    value: "ELECTRONIC_CLAIM",
    label: "Electronic",
  },
  {
    value: "PAPER_CLAIM",
    label: "Paper",
  },
];

// Passed / Pending / Failed
// Scrub Status filter removed from the Claims listing UI (replaced by Claim ID
// filter); kept here commented out in case scrub-status filtering returns.
// export const SCRUB_STATUS_LIST: Option[] = [
//   {
//     value: "PASSED",
//     label: "Passed",
//   },
//   {
//     value: "PENDING",
//     label: "Pending",
//   },
//   {
//     value: "FAILED",
//     label: "Failed",
//   },
// ];

export const convertClaimCodeToUpdateClaimPayload = (sourceData: {
  uuid: string;
  code: string;
  description: string;
  type?: string;
  active: boolean;
  archive: boolean;
}) => {
  return {
    uuid: undefined,
    procedureCode: {
      [sourceData.uuid]: `${sourceData.code} - ${sourceData.description}`,
    },
    // carry the code classification (CPT/HCPCS) so the badge reflects it
    type: sourceData.type,
    diagnosisFirstPointers: "",
    diagnosisSecondPointers: "",
    diagnosisThirdPointers: "",
    diagnosisFourthPointers: "",
    medicalCodeModifiers: "",
    modifiers: {},
    quantity: 1,
    total: 0,
    charges: 0,
    discount: 0,
    discountType: undefined,
    active: sourceData.active,
    archive: sourceData.archive,
  };
};

export const convertDiagnosisCodesData = (data?: { [key: string]: string }) => {
  if (!data) return [];
  return Object.entries(data).map(([uuid, value]) => {
    const [code, ...descriptionParts] = value.split(" - ");
    const description = descriptionParts.join(" - ");
    return {
      uuid,
      codeVersion: null,
      type: "ICD",
      code,
      description: description.trim(),
      source: null,
      active: true,
      archive: false,
      errorMessage: null,
      drugId: null,
      formType: null,
      drugName: null,
      strength: null,
    };
  });
};

/**
 * Clearinghouses (Waystar, Claim MD) return every message of a claim response
 * inside one string, joined by a pipe:
 *
 *   "NCCI - Procedure Code [99212] requires an appropriate modifier when
 *    procedure [59012] present. | Diagnosis code invalid [E802]."
 *   "Invalid or Unsupported PayerID [66666]  | Accident Date Required if
 *    Employment or Auto or Other Accident is [Y]."
 */
const CLEARING_HOUSE_RESPONSE_SEPARATOR = "|";

/** Placeholder used when a claim carries no clearinghouse response text. */
export const NO_CLEARING_HOUSE_RESPONSE = "No errors found";

/**
 * Splits a pipe-delimited clearinghouse response into its individual messages,
 * so the UI can list one per line instead of rendering the whole response as a
 * single paragraph.
 *
 * @param response - Raw response string as stored on the claim (`error`).
 * @returns One trimmed message per entry; an empty array when the response is
 *   missing, blank, or holds nothing but separators.
 *
 * @example
 * parseClearingHouseResponse("Billing NPI Fails LUHN check. | From Date is required.")
 * // => ["Billing NPI Fails LUHN check.", "From Date is required."]
 */
export const parseClearingHouseResponse = (
  response?: string | null
): string[] => {
  if (!response?.trim()) return [];

  return (
    response
      .split(CLEARING_HOUSE_RESPONSE_SEPARATOR)
      // Payers pad messages that carry no code with an empty bracket pair;
      // brackets holding an actual code (`[99212]`) are left untouched.
      .map((message) => message.replace(/\[\]/g, "").trim())
      .filter(Boolean)
  );
};

/**
 * Claim type values accepted by `getAllClaimV2` / `getClaimStatusCounts`.
 *
 * Previously exported by the generated SDK as `GetAllClaimV2ClaimTypeEnum`.
 * The regenerated spec now types `claimType` as a plain `Array<string>`, so
 * the values live here instead of being re-derived from the client.
 */
export const CLAIM_TYPE = {
  ElectronicClaim: "ELECTRONIC_CLAIM",
  PaperClaim: "PAPER_CLAIM",
} as const;

export type ClaimType = (typeof CLAIM_TYPE)[keyof typeof CLAIM_TYPE];
